// Copyright (c) Docugami, Inc. All rights reserved.

import axios from "axios";
import { OperationCancelledError, RetryConfiguration, RetryPromise, RetryResult } from "./RetryPromise";
import { StorageAccessor } from "./StorageAccessor";

export interface TrackedRequest {
    id: string;
    url: string;
    timestamp: Date;
}

export interface ActiveRequest<T> {
    item: TrackedRequest;
    promise: RetryPromise<T>;
    abort: AbortController;
}

export class TrackedRequests<T> {
    private readonly _key: string;
    private _isRunning: boolean = false;
    private readonly _config: RetryConfiguration<T>;
    private _activeItems: ActiveRequest<T>[] = [];
    private _orgId: string = '';
    private _resultCallback: ((item: TrackedRequest, result: RetryResult<T>) => void) | null = null;
    get activeRequests(): ActiveRequest<T>[] { return this._activeItems; }

    constructor(key: string, configuration: RetryConfiguration<T>, orgId: string) {
        this._orgId = orgId;
        this._key = key;
        this._config = configuration;
    }

    destructor() {
        this.stop();
    }

    public start(onRequestResult: (item: TrackedRequest, result: RetryResult<T>) => void) {
        if (this._isRunning) {
            return;
        }

        this._isRunning = true;
        this._resultCallback = onRequestResult;
        const accessor = new StorageAccessor(window.localStorage, this._orgId);
        const items = accessor.getObject<TrackedRequest[]>(this._key, [])!;

        for (const item of items) {
            this.track(item);
        }
    }

    public stop() {
        this._isRunning = false;
        this._resultCallback = null;
        for (const activeItem of this._activeItems) {
            this.cancel(activeItem.item.id);
        }
    }

    public untrack(id: string) {
        this.cancel(id);
        this.delete(id);
    }

    public async track(item: TrackedRequest): Promise<void> {
        item.timestamp = new Date();
        this.add(item);

        if (!this._isRunning || !this._resultCallback) {
            return;
        }

        const execute = async () => {
            const response = await axios.get(item.url);
            return response.data;
        }

        const retryPromise = new RetryPromise<T>(execute, this._config);
        const callback = (result: RetryResult<T>) => {
            if (!!this._resultCallback) {
                this._resultCallback(item, result);
            }
        };

        const unregisterEvent = retryPromise.onExecuted.addListener(callback);
        const abortController = new AbortController();

        try {
            this._activeItems.push({
                item: item,
                promise: retryPromise,
                abort: abortController
            });

            await retryPromise.execute(abortController.signal);
            this.untrack(item.id);
        }
        catch (error) {
            if (!(error instanceof OperationCancelledError)) {
                this.untrack(item.id);
                throw error;
            }
        }
        finally {
            unregisterEvent();

            const activeIndex = this._activeItems.findIndex(x => x.item.id === item.id);
            if (activeIndex !== -1) {
                this._activeItems = this._activeItems.splice(activeIndex);
            }
        }
    }

    private cancel(id: string) {
        const activeIndex = this._activeItems.findIndex(x => x.item.id === id);
        if (activeIndex !== -1) {
            this._activeItems[activeIndex].abort.abort();
        }
    }

    private delete(id: string) {
        const accessor = new StorageAccessor(window.localStorage, this._orgId);
        let items = accessor.getObject<TrackedRequest[]>(this._key, [])!;
        items = [...items.filter(x => x.id !== id)];
        accessor.setObject(this._key, items);
    }

    private add(item: TrackedRequest) {
        const accessor = new StorageAccessor(window.localStorage, this._orgId);
        let items = accessor.getObject<TrackedRequest[]>(this._key, [])!;
        items = [...items.filter(x => x.id !== item.id), item];
        accessor.setObject(this._key, items);
    }
}
